/**
 * ⚠️  INTERNAL USE ONLY
 * Known-Good Reference editor — /admin/repair-knowledge-base/ref/:refId
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AdminLoginGate, isAdminAuthed, clearAdminAuth } from '../../components/AdminLoginGate';
import {
  useRepairReferences,
  fetchRepairReference,
  createRepairReference,
  saveRepairReference,
  deleteRepairReference,
  uploadRefMedia,
  updateRefMedia,
  deleteRefMedia,
  addAnnotation,
  updateAnnotation,
  deleteAnnotation,
  useTaxonomy,
  familiesForManufacturer,
  modelsForFamily,
  revisionsForFamily,
  REF_MEASUREMENT_TYPES,
  REF_CATEGORIES,
  REF_SOURCE_TYPES,
  REF_VERIFICATION,
  measurementTypeLabel,
} from '../../lib/repair-kb-client';
import type {
  RepairReference,
  RepairReferenceDraft,
  RefMedia,
  RefAnnotation,
  RefMeasurement,
  RefMeasurementType,
  RefCategory,
  RefSourceType,
  RefVerification,
  RepairStatus,
  KbTaxonomy,
} from '../../lib/repair-kb-client';
import {
  ArrowLeft, Plus, Trash2, Loader, Upload, Save,
  X, Check, MapPin, FileText, Star,
  ChevronDown, ChevronUp,
} from '../../lib/icons';

// ── Styles ────────────────────────────────────────────────────────────────────

const inp = 'w-full bg-black/40 border border-gray-700 rounded-xl px-3 py-2 text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 text-sm';
const sel = inp + ' cursor-pointer';
const btn = (v: 'primary' | 'secondary' | 'danger') => {
  const base = 'flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-colors disabled:opacity-50';
  if (v === 'primary')   return base + ' bg-[#FF1744] hover:bg-[#D50000] text-white';
  if (v === 'secondary') return base + ' bg-gray-800 hover:bg-gray-700 border border-gray-700 text-[#E0E0E0]';
  return base + ' bg-red-900/40 hover:bg-red-800/60 border border-red-800 text-red-300';
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface FormState {
  // taxonomy (new cascading dropdowns)
  manufacturer_id:   string;
  device_family_id:  string;
  model_id:          string;
  board_revision_id: string;
  // text back-compat fields (populated from taxonomy on save)
  device_family:  string;
  device_model:   string;
  board_revision: string;
  // core content
  title:              string;
  measurement_type:   RefMeasurementType | '';
  measurement_type_label: string;
  notes:              string;
  status:             RepairStatus;
  slug:               string;
  // preserved but not shown in UI (prevent overwriting existing data)
  seo_title:            string;
  seo_description:      string;
  category:             RefCategory | '';
  testing_conditions:   string;
  source_type:          RefSourceType;
  source_url:           string;
  verification_status:  RefVerification;
  confirmed_board_count: number;
  internal_notes:       string;
  tags:                 string[];
  measurements:         RefMeasurement[];
}

function blankForm(): FormState {
  return {
    manufacturer_id: '', device_family_id: '', model_id: '', board_revision_id: '',
    device_family: '', device_model: '', board_revision: '',
    title: '', measurement_type: '', measurement_type_label: '',
    notes: '', status: 'draft', slug: '',
    seo_title: '', seo_description: '',
    category: '', testing_conditions: '',
    source_type: 'gamefix', source_url: '',
    verification_status: 'observed', confirmed_board_count: 1,
    internal_notes: '', tags: [], measurements: [],
  };
}

function buildPayload(
  form: FormState,
  taxonomy: KbTaxonomy,
  statusOverride?: RepairStatus,
): RepairReferenceDraft {
  const family = taxonomy.device_families.find(f => f.id === form.device_family_id);
  const model  = taxonomy.models.find(m => m.id === form.model_id);
  const board  = taxonomy.board_revisions.find(b => b.id === form.board_revision_id);
  const mtype  = measurementTypeLabel(form.measurement_type || 'other');
  const deviceParts = [model?.name, family?.name].filter(Boolean);
  const seo_title = form.title
    ? `${form.title} | GameFix Known-Good Reference`
    : `${[...deviceParts, mtype].join(' ')} Reference | GameFix`;
  const seo_description =
    `Known-good ${mtype.toLowerCase()} reference` +
    (deviceParts.length ? ` for the ${deviceParts.join(' ')}` : '') +
    (board?.name ? ` (${board.name})` : '') +
    '. Board-level diagnostic measurements verified by GameFix technicians.';
  const notes = form.measurement_type === 'other' && form.measurement_type_label.trim()
    ? `[${form.measurement_type_label.trim()}]\n${form.notes}`.trim()
    : form.notes;
  return {
    status:               statusOverride ?? form.status,
    title:                form.title,
    seo_title,
    seo_description,
    device_family:        family?.name  ?? form.device_family,
    device_model:         model?.name   ?? form.device_model,
    board_revision:       board?.name   ?? form.board_revision,
    device_family_id:     form.device_family_id  || null,
    model_id:             form.model_id           || null,
    board_revision_id:    form.board_revision_id  || null,
    category:             form.category,
    measurement_type:     form.measurement_type,
    testing_conditions:   form.testing_conditions,
    notes,
    source_type:          form.source_type,
    source_url:           form.source_url,
    verification_status:  form.verification_status,
    confirmed_board_count: form.confirmed_board_count,
    internal_notes:       form.internal_notes,
    tags:                 form.tags,
    measurements:         form.measurements,
    ...(form.slug ? { slug: form.slug } : {}),
  };
}

// ── Annotation overlay ────────────────────────────────────────────────────────

const MARKER_COLORS = [
  '#FF1744','#00BFFF','#39FF14','#FF9800','#E040FB',
  '#00E5FF','#FFD740','#FF6E40','#B2FF59','#EA80FC',
];

interface AnnotationOverlayProps {
  refId: string;
  media: RefMedia;
  onAnnotationsChange: (mediaId: string, annotations: RefAnnotation[]) => void;
  onExpired: () => void;
}

function AnnotationOverlay({ refId, media, onAnnotationsChange, onExpired }: AnnotationOverlayProps) {
  const imgRef  = useRef<HTMLImageElement>(null);
  const [annotations, setAnnotations] = useState<RefAnnotation[]>(media.repair_reference_annotations ?? []);
  const [selected, setSelected]       = useState<string | null>(null);
  const [pending, setPending]         = useState<{ x: number; y: number } | null>(null);
  const [saving, setSaving]           = useState(false);
  const [editForm, setEditForm]       = useState<Partial<RefAnnotation>>({});

  const syncUp = useCallback((next: RefAnnotation[]) => {
    setAnnotations(next);
    onAnnotationsChange(media.id!, next);
  }, [media.id, onAnnotationsChange]);

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (selected) { setSelected(null); return; }
    const rect = imgRef.current!.getBoundingClientRect();
    const x = parseFloat(((e.clientX - rect.left) / rect.width  * 100).toFixed(2));
    const y = parseFloat(((e.clientY - rect.top)  / rect.height * 100).toFixed(2));
    setPending({ x, y });
    setEditForm({ label: '', measurement_type: 'diode-mode', measured_value: '', units: '', notes: '' });
  };

  const handleSaveNew = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const created = await addAnnotation(refId, media.id!, {
        label:            editForm.label            ?? '',
        x_percent:        pending.x,
        y_percent:        pending.y,
        measurement_type: editForm.measurement_type ?? 'diode-mode',
        measured_value:   editForm.measured_value   ?? '',
        units:            editForm.units            ?? '',
        notes:            editForm.notes            ?? '',
        sort_order:       annotations.length,
      });
      syncUp([...annotations, created]);
      setPending(null);
      setEditForm({});
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert('Failed to save annotation: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSelected = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await updateAnnotation(refId, media.id!, selected, editForm);
      syncUp(annotations.map(a => a.id === selected ? { ...a, ...updated } : a));
      setSelected(null);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert('Failed to update: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (annotId: string) => {
    setSaving(true);
    try {
      await deleteAnnotation(refId, media.id!, annotId);
      syncUp(annotations.filter(a => a.id !== annotId));
      setSelected(null);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert('Failed to delete: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const selectAnnotation = (a: RefAnnotation) => {
    setSelected(a.id!);
    setPending(null);
    setEditForm({
      label: a.label, measurement_type: a.measurement_type,
      measured_value: a.measured_value, units: a.units, notes: a.notes,
    });
  };

  const activeAnnot = annotations.find(a => a.id === selected);

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Click the image to place a measurement marker. Click an existing marker to edit it.</p>

      {/* Image with markers */}
      <div className="relative inline-block w-full select-none">
        <img
          ref={imgRef}
          src={media.public_url}
          alt={media.caption || 'Board photo'}
          className="w-full rounded-xl border border-gray-700 cursor-crosshair"
          onClick={handleImageClick}
          draggable={false}
        />

        {/* Existing markers */}
        {annotations.map((a, i) => {
          const color = MARKER_COLORS[i % MARKER_COLORS.length];
          const isSelected = a.id === selected;
          return (
            <button
              key={a.id}
              onClick={(e) => { e.stopPropagation(); selectAnnotation(a); }}
              style={{
                position: 'absolute',
                left: `${a.x_percent}%`,
                top:  `${a.y_percent}%`,
                transform: 'translate(-50%, -50%)',
                background: color,
                border: isSelected ? '2px solid white' : '2px solid rgba(0,0,0,0.6)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.8)',
              }}
              className="w-6 h-6 rounded-full flex items-center justify-center text-black font-bold text-xs z-10 hover:scale-125 transition-transform"
              title={`${a.label}: ${a.measured_value} ${a.units}`}
            >
              {i + 1}
            </button>
          );
        })}

        {/* Pending new marker */}
        {pending && (
          <div
            style={{
              position: 'absolute',
              left: `${pending.x}%`,
              top:  `${pending.y}%`,
              transform: 'translate(-50%, -50%)',
              background: '#FF1744',
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(0,0,0,0.8)',
            }}
            className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs z-10 animate-pulse"
          >
            +
          </div>
        )}
      </div>

      {/* Edit panel for pending new marker */}
      {pending && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#FF1744]">New marker at ({pending.x.toFixed(1)}%, {pending.y.toFixed(1)}%)</span>
            <button onClick={() => setPending(null)} className="text-gray-500 hover:text-gray-300"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Label / Reference</label>
              <input className={inp} placeholder="Pin 1, F3502…" value={editForm.label ?? ''}
                onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Measurement Type</label>
              <select className={sel} value={editForm.measurement_type ?? ''}
                onChange={e => setEditForm(f => ({ ...f, measurement_type: e.target.value as RefMeasurementType }))}>
                {REF_MEASUREMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Value</label>
              <input className={inp} placeholder="0.612" value={editForm.measured_value ?? ''}
                onChange={e => setEditForm(f => ({ ...f, measured_value: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Units</label>
              <input className={inp} placeholder="diode, V, Ω…" value={editForm.units ?? ''}
                onChange={e => setEditForm(f => ({ ...f, units: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Notes (optional)</label>
            <input className={inp} placeholder="Expected known-good reading…" value={editForm.notes ?? ''}
              onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSaveNew} disabled={saving} className={btn('primary')}>
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Place Marker
            </button>
          </div>
        </div>
      )}

      {/* Edit panel for existing selected marker */}
      {selected && activeAnnot && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#00BFFF]">
              Edit marker #{annotations.findIndex(a => a.id === selected) + 1}
            </span>
            <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-300"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Label / Reference</label>
              <input className={inp} value={editForm.label ?? ''}
                onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Measurement Type</label>
              <select className={sel} value={editForm.measurement_type ?? ''}
                onChange={e => setEditForm(f => ({ ...f, measurement_type: e.target.value as RefMeasurementType }))}>
                {REF_MEASUREMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Value</label>
              <input className={inp} value={editForm.measured_value ?? ''}
                onChange={e => setEditForm(f => ({ ...f, measured_value: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Units</label>
              <input className={inp} value={editForm.units ?? ''}
                onChange={e => setEditForm(f => ({ ...f, units: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Notes</label>
            <input className={inp} value={editForm.notes ?? ''}
              onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <button onClick={handleUpdateSelected} disabled={saving} className={btn('primary')}>
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Update
            </button>
            <button onClick={() => handleDelete(selected)} disabled={saving} className={btn('danger')}>
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>
      )}

      {/* Annotation summary table */}
      {annotations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left border border-gray-800 rounded-lg overflow-hidden">
            <thead className="bg-gray-800 text-gray-400">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Units</th>
                <th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {annotations.map((a, i) => (
                <tr key={a.id}
                  className={`border-t border-gray-800 cursor-pointer transition-colors ${a.id === selected ? 'bg-gray-800' : 'hover:bg-gray-900'}`}
                  onClick={() => selectAnnotation(a)}
                >
                  <td className="px-3 py-2">
                    <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-black font-bold"
                      style={{ background: MARKER_COLORS[i % MARKER_COLORS.length] }}>
                      {i + 1}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#E0E0E0]">{a.label || '—'}</td>
                  <td className="px-3 py-2 text-gray-400">{a.measurement_type}</td>
                  <td className="px-3 py-2 text-[#39FF14] font-mono">{a.measured_value || '—'}</td>
                  <td className="px-3 py-2 text-gray-400">{a.units || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 truncate max-w-[120px]">{a.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Media panel ───────────────────────────────────────────────────────────────

interface MediaPanelProps {
  refId: string | undefined;
  media: RefMedia[];
  onChange: (media: RefMedia[]) => void;
  onExpired: () => void;
  onAutoSave: () => Promise<string>;
}

function MediaPanel({ refId, media, onChange, onExpired, onAutoSave }: MediaPanelProps) {
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const dragSrcIdx    = useRef<number | null>(null);
  const [uploading, setUploading]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [uploads, setUploads] = useState<{ name: string; pct: number; done: boolean; err: string | null }[]>([]);

  const handleCaptionChange = (id: string, value: string) => {
    setCaptions(prev => ({ ...prev, [id]: value }));
  };

  const handleCaptionBlur = async (m: RefMedia) => {
    if (!m.id || !refId) return;
    const draft = captions[m.id];
    if (draft === undefined || draft === (m.caption ?? '')) return;
    try {
      await updateRefMedia(refId, m.id, { caption: draft });
      onChange(media.map(x => x.id === m.id ? { ...x, caption: draft } : x));
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert('Caption save failed: ' + msg);
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    let id = refId;
    if (!id) {
      try {
        id = await onAutoSave();
      } catch (err) {
        alert('Auto-save failed: ' + (err as Error).message);
        return;
      }
    }
    const fileArr = Array.from(files);
    setUploading(true);
    setUploads(fileArr.map(f => ({ name: f.name, pct: 0, done: false, err: null })));
    const nextMedia = [...media];
    for (let i = 0; i < fileArr.length; i++) {
      const file = fileArr[i];
      try {
        const newMedia = await uploadRefMedia(id, file, pct => {
          setUploads(u => u.map((x, j) => j === i ? { ...x, pct } : x));
          setProgress(pct);
        });
        nextMedia.push(newMedia);
        onChange([...nextMedia]);
        setUploads(u => u.map((x, j) => j === i ? { ...x, done: true, pct: 100 } : x));
      } catch (err) {
        const msg = (err as Error).message;
        if (msg.toLowerCase().includes('unauthorized')) { setUploading(false); setUploads([]); onExpired(); return; }
        setUploads(u => u.map((x, j) => j === i ? { ...x, err: msg } : x));
      }
    }
    setUploading(false);
    setProgress(0);
    setTimeout(() => setUploads([]), 3000);
  };

  const handleDelete = async (m: RefMedia) => {
    if (!confirm(`Delete this photo?`)) return;
    if (!refId) return;
    try {
      await deleteRefMedia(refId, m.id!);
      onChange(media.filter(x => x.id !== m.id));
      if (expandedId === m.id) setExpandedId(null);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert('Delete failed: ' + msg);
    }
  };

  const handleSetPrimary = async (idx: number) => {
    if (!refId || idx === 0) return;
    const next = [...media];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    const withOrder = next.map((m, i) => ({ ...m, sort_order: i }));
    onChange(withOrder);
    for (let i = 0; i < withOrder.length; i++) {
      const m = withOrder[i];
      if (m.id) {
        try { await updateRefMedia(refId, m.id, { sort_order: i }); } catch { /* silent */ }
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    dragSrcIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    if (e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDragOverIdx(idx);
  };

  const handleDrop = async (e: React.DragEvent, toIdx: number) => {
    if (e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    const fromIdx = dragSrcIdx.current;
    setDragOverIdx(null);
    if (fromIdx === null || fromIdx === toIdx || !refId) { dragSrcIdx.current = null; return; }
    const next = [...media];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const withOrder = next.map((m, i) => ({ ...m, sort_order: i }));
    onChange(withOrder);
    dragSrcIdx.current = null;
    for (let i = 0; i < withOrder.length; i++) {
      const m = withOrder[i];
      if (m.id) {
        try { await updateRefMedia(refId, m.id, { sort_order: i }); } catch { /* silent */ }
      }
    }
  };

  const handleAnnotationsChange = (mediaId: string, annotations: RefAnnotation[]) => {
    onChange(media.map(m => m.id === mediaId
      ? { ...m, repair_reference_annotations: annotations }
      : m
    ));
  };

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
        onDrop={e => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) handleUpload(e.dataTransfer.files); }}
        className="border-2 border-dashed border-gray-700 hover:border-[#FF1744]/50 rounded-xl p-6 text-center cursor-pointer transition-colors"
      >
        <input ref={fileInputRef} type="file" accept="image/*,video/mp4,video/quicktime"
          multiple className="hidden" onChange={e => handleUpload(e.target.files)} />
        {uploading ? (
          <div className="space-y-2">
            <Loader className="h-6 w-6 animate-spin text-[#FF1744] mx-auto" />
            {uploads.length <= 1 ? (
              <>
                <p className="text-sm text-gray-400">Uploading… {progress}%</p>
                <div className="w-full bg-gray-800 rounded-full h-1.5">
                  <div className="bg-[#FF1744] h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : (
              <div className="text-left max-w-xs mx-auto space-y-1.5 mt-1">
                {uploads.map((u, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.err ? 'bg-red-500' : u.done ? 'bg-green-500' : 'bg-[#FF1744] animate-pulse'}`} />
                    <span className="text-gray-400 truncate flex-1 max-w-[160px]">{u.name}</span>
                    <span className="text-gray-500 flex-shrink-0 font-mono">
                      {u.err ? '✕' : u.done ? '✓' : `${u.pct}%`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <Upload className="h-6 w-6 text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Drop board photos here or click to browse</p>
            <p className="text-xs text-gray-600 mt-1">JPG, PNG, WEBP, MP4 — multiple files OK</p>
          </>
        )}
      </div>

      {/* Photo list with drag-to-reorder */}
      {media.length > 0 && (
        <p className="text-xs text-gray-600">Drag photos to reorder · First photo is the primary image</p>
      )}
      {media.map((m, idx) => (
        <div
          key={m.id ?? idx}
          draggable
          onDragStart={e => handleDragStart(e, idx)}
          onDragOver={e => handleDragOver(e, idx)}
          onDrop={e => handleDrop(e, idx)}
          onDragEnd={() => { setDragOverIdx(null); dragSrcIdx.current = null; }}
          className={`bg-gray-900 border rounded-xl overflow-hidden transition-all cursor-grab active:cursor-grabbing ${
            dragOverIdx === idx ? 'border-[#FF1744]/60 scale-[1.01]' : 'border-gray-800'
          }`}
        >
          {/* Full photo — always visible */}
          <div className="bg-black">
            <img
              src={m.public_url}
              alt={m.caption || `Photo ${idx + 1}`}
              className="w-full max-h-72 object-contain"
            />
          </div>

          {/* Caption + action row */}
          <div className="px-3 pt-2.5 pb-1 border-t border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              {idx === 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-[#FF1744]/20 text-[#FF1744] rounded font-medium flex-shrink-0">Primary</span>
              )}
              {(m.repair_reference_annotations?.length ?? 0) > 0 && (
                <span className="text-xs text-[#00BFFF]/70 flex-shrink-0">
                  {m.repair_reference_annotations!.length} annotation{m.repair_reference_annotations!.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <input
              className="w-full bg-black/40 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 mb-2"
              placeholder="Add a caption…"
              value={m.id && captions[m.id] !== undefined ? captions[m.id] : (m.caption ?? '')}
              onChange={e => m.id && handleCaptionChange(m.id, e.target.value)}
              onBlur={() => handleCaptionBlur(m)}
            />
          </div>
          <div className="flex items-center gap-1.5 px-3 pb-2.5">
              {idx !== 0 && (
                <button
                  onClick={() => handleSetPrimary(idx)}
                  className="text-xs px-2 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-[#FF1744] transition-colors flex items-center gap-1"
                  title="Set as primary"
                >
                  <Star className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={() => setExpandedId(expandedId === m.id ? null : m.id!)}
                className="text-xs px-2.5 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-[#00BFFF] transition-colors flex items-center gap-1"
              >
                <MapPin className="h-3 w-3" />
                Annotate
                {expandedId === m.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              <button
                onClick={() => handleDelete(m)}
                className="text-xs px-2.5 py-1.5 bg-red-900/30 hover:bg-red-800/50 border border-red-800/50 rounded-lg text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </button>
          </div>

          {expandedId === m.id && (
            <div className="border-t border-gray-800 p-4">
              {m.id && refId ? (
                <AnnotationOverlay
                  refId={refId}
                  media={m}
                  onAnnotationsChange={handleAnnotationsChange}
                  onExpired={onExpired}
                />
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">Save the reference first to enable annotations.</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Reference editor ──────────────────────────────────────────────────────────

function RefEditor({ refId: rawId, onExpired }: { refId: string | undefined; onExpired: () => void }) {
  const navigate  = useNavigate();
  const isNew     = !rawId || rawId === 'new';
  const savedId   = useRef<string | undefined>(isNew ? undefined : rawId);

  const { taxonomy, loading: taxLoading } = useTaxonomy();
  const [form, setForm]     = useState<FormState>(blankForm());
  const [media, setMedia]   = useState<RefMedia[]>([]);
  const [rawRef, setRawRef] = useState<RepairReference | null>(null);
  const [loading, setLoading]   = useState(!isNew);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // Load raw reference data
  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    fetchRepairReference(rawId!, true)
      .then(r => setRawRef(r))
      .catch(e => {
        const msg = (e as Error).message;
        if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
        showToast(msg, false);
        setLoading(false);
      });
  }, [rawId, isNew, onExpired]);

  // Populate form once both ref and taxonomy are ready
  useEffect(() => {
    if (!rawRef || taxLoading) return;
    const family = taxonomy.device_families.find(f => f.id === rawRef.device_family_id);
    setForm({
      manufacturer_id:   family?.manufacturer_id ?? '',
      device_family_id:  rawRef.device_family_id  ?? '',
      model_id:          rawRef.model_id           ?? '',
      board_revision_id: rawRef.board_revision_id  ?? '',
      device_family:     rawRef.device_family,
      device_model:      rawRef.device_model,
      board_revision:    rawRef.board_revision,
      title:             rawRef.title,
      measurement_type:  rawRef.measurement_type,
      measurement_type_label: '',
      notes:             rawRef.notes,
      status:            rawRef.status,
      slug:              rawRef.slug,
      seo_title:         rawRef.seo_title,
      seo_description:   rawRef.seo_description,
      category:          rawRef.category,
      testing_conditions: rawRef.testing_conditions,
      source_type:       rawRef.source_type,
      source_url:        rawRef.source_url,
      verification_status:   rawRef.verification_status,
      confirmed_board_count: rawRef.confirmed_board_count,
      internal_notes:    rawRef.internal_notes ?? '',
      tags:              rawRef.tags,
      measurements:      rawRef.repair_reference_measurements ?? [],
    });
    setMedia(rawRef.repair_reference_media ?? []);
    setLoading(false);
  }, [rawRef, taxonomy, taxLoading]);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  // Cascading taxonomy selection helpers
  const selectManufacturer = useCallback((mfrId: string) => {
    setForm(prev => ({
      ...prev,
      manufacturer_id: mfrId,
      device_family_id: '', model_id: '', board_revision_id: '',
      device_family: '', device_model: '', board_revision: '',
    }));
  }, []);

  const selectFamily = useCallback((familyId: string) => {
    const family = taxonomy.device_families.find(f => f.id === familyId);
    setForm(prev => ({
      ...prev,
      device_family_id: familyId,
      model_id: '', board_revision_id: '',
      device_family: family?.name ?? '',
      device_model: '', board_revision: '',
    }));
  }, [taxonomy]);

  const selectModel = useCallback((modelId: string) => {
    const model = taxonomy.models.find(m => m.id === modelId);
    setForm(prev => ({ ...prev, model_id: modelId, device_model: model?.name ?? '' }));
  }, [taxonomy]);

  const selectRevision = useCallback((revId: string) => {
    const rev = taxonomy.board_revisions.find(b => b.id === revId);
    setForm(prev => ({ ...prev, board_revision_id: revId, board_revision: rev?.name ?? '' }));
  }, [taxonomy]);

  const doSave = async (statusOverride?: RepairStatus): Promise<string | null> => {
    if (saving) return null;
    if (!form.title.trim()) { showToast('Title is required.', false); return null; }
    if (!form.measurement_type) { showToast('Measurement Type is required.', false); return null; }
    setSaving(true);
    try {
      const payload = buildPayload(form, taxonomy, statusOverride);
      if (isNew && !savedId.current) {
        const created = await createRepairReference(payload);
        savedId.current = created.id;
        showToast('Created!', true);
        navigate(`/admin/repair-knowledge-base/ref/${created.id}`, { replace: true });
        return created.id;
      } else {
        const id = savedId.current ?? rawId!;
        await saveRepairReference(id, payload);
        if (statusOverride === 'published') set('status', 'published');
        showToast(statusOverride === 'published' ? 'Published!' : 'Saved!', true);
        return id;
      }
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return null; }
      showToast(msg, false);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => doSave();
  const handlePublish   = () => doSave('published');
  const handleSaveNew   = async () => {
    const ok = await doSave();
    if (ok) {
      navigate('/admin/repair-knowledge-base/ref/new');
      setForm(blankForm());
      setMedia([]);
      savedId.current = undefined;
    }
  };

  // Auto-save for media panel (returns new ID, called when dropping photos on unsaved record)
  const autoSaveForMedia = useCallback(async (): Promise<string> => {
    const payload = buildPayload(form, taxonomy, 'draft');
    const created = await createRepairReference(payload);
    savedId.current = created.id;
    navigate(`/admin/repair-knowledge-base/ref/${created.id}`, { replace: true });
    return created.id;
  }, [form, taxonomy, navigate]);

  const currentId = savedId.current ?? (!isNew ? rawId : undefined);

  // Derived taxonomy options
  const families  = form.manufacturer_id ? familiesForManufacturer(taxonomy, form.manufacturer_id) : [];
  const models    = form.device_family_id ? modelsForFamily(taxonomy, form.device_family_id) : [];
  const revisions = form.device_family_id ? revisionsForFamily(taxonomy, form.device_family_id) : [];

  if (loading || (taxLoading && !isNew)) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-[#FF1744]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0D0D0D]/95 border-b border-gray-800 backdrop-blur-sm">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-6 pt-2 flex items-center gap-1.5 text-xs text-gray-600">
          <Link to="/admin/edit-site" className="hover:text-gray-400 transition-colors">Admin</Link>
          <span>›</span>
          <Link to="/admin/repair-knowledge-base" className="hover:text-gray-400 transition-colors">Repair Knowledge Base</Link>
          <span>›</span>
          <Link to="/admin/repair-knowledge-base/refs" className="hover:text-gray-400 transition-colors">Known-Good References</Link>
          <span>›</span>
          <span className="text-gray-400">{isNew ? 'New' : 'Edit'}</span>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/repair-knowledge-base/refs"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors">
              <ArrowLeft className="h-4 w-4" /> Known-Good References
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSaveNew} disabled={saving} className={btn('secondary') + ' text-xs'}>
              Save &amp; Add Another
            </button>
            <button onClick={handleSaveDraft} disabled={saving} className={btn('secondary')}>
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Draft
            </button>
            <button onClick={handlePublish} disabled={saving || form.status === 'published'} className={btn('primary')}>
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {form.status === 'published' ? 'Published' : 'Publish'}
            </button>
          </div>
        </div>
        {toast && (
          <div className={`border-t px-6 py-2 text-sm flex items-center gap-2 ${
            toast.ok ? 'bg-green-900/30 border-green-800 text-green-300' : 'bg-red-900/30 border-red-800 text-red-300'
          }`}>
            {toast.ok ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
            {toast.msg}
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">

        {/* ── Title ─────────────────────────────────────────────────────── */}
        <div>
          <input
            className="w-full bg-black/40 border border-gray-700 rounded-xl px-4 py-3 text-lg text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
            placeholder="Reference title…"
            value={form.title}
            onChange={e => set('title', e.target.value)}
          />
        </div>

        {/* ── Taxonomy ──────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Device</h2>
          <div className="grid grid-cols-2 gap-3">
            {/* Manufacturer */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Manufacturer</label>
              {taxLoading ? (
                <div className={sel + ' flex items-center gap-2 text-gray-600'}><Loader className="h-3 w-3 animate-spin" /> Loading…</div>
              ) : (
                <select className={sel} value={form.manufacturer_id} onChange={e => selectManufacturer(e.target.value)}>
                  <option value="">Select manufacturer…</option>
                  {taxonomy.manufacturers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              )}
            </div>
            {/* Device Family */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Device Family</label>
              <select
                className={sel}
                value={form.device_family_id}
                onChange={e => selectFamily(e.target.value)}
                disabled={!form.manufacturer_id}
              >
                <option value="">{form.manufacturer_id ? 'Select device family…' : '— select manufacturer first —'}</option>
                {families.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            {/* Model */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Model</label>
              <select
                className={sel}
                value={form.model_id}
                onChange={e => selectModel(e.target.value)}
                disabled={!form.device_family_id}
              >
                <option value="">{form.device_family_id ? 'All models / not specified' : '— select device family first —'}</option>
                {models.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            {/* Board Revision */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Board Revision</label>
              <select
                className={sel}
                value={form.board_revision_id}
                onChange={e => selectRevision(e.target.value)}
                disabled={!form.device_family_id}
              >
                <option value="">{form.device_family_id ? 'All revisions / not specified' : '— select device family first —'}</option>
                {revisions.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* ── Measurement Type ──────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Measurement Type *</h2>
          <div className="grid grid-cols-3 gap-3">
            {REF_MEASUREMENT_TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => set('measurement_type', t.value)}
                className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                  form.measurement_type === t.value
                    ? 'bg-[#FF1744]/15 border-[#FF1744]/60 text-[#FF1744]'
                    : 'bg-black/30 border-gray-700 text-gray-400 hover:border-gray-500 hover:text-[#E0E0E0]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {form.measurement_type === 'other' && (
            <input
              className={inp}
              placeholder="Describe measurement type…"
              value={form.measurement_type_label}
              onChange={e => set('measurement_type_label', e.target.value)}
            />
          )}
        </section>

        {/* ── Notes ─────────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Notes</h2>
          <textarea
            className={inp + ' resize-y min-h-[80px]'}
            placeholder="Test conditions, pin references, caveats, expected range…"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
          />
        </section>

        {/* ── Photos ────────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Photos</h2>
          <MediaPanel
            refId={currentId}
            media={media}
            onChange={setMedia}
            onExpired={onExpired}
            onAutoSave={autoSaveForMedia}
          />
        </section>

        {/* Bottom action bar */}
        <div className="flex items-center gap-3 pb-8 justify-end border-t border-gray-800 pt-6">
          <button onClick={handleSaveNew} disabled={saving} className={btn('secondary') + ' text-xs'}>
            Save &amp; Add Another
          </button>
          <button onClick={handleSaveDraft} disabled={saving} className={btn('secondary')}>
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Draft
          </button>
          <button onClick={handlePublish} disabled={saving || form.status === 'published'} className={btn('primary')}>
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {form.status === 'published' ? 'Published' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk Create Modal ─────────────────────────────────────────────────────────

type BulkRowStatus = 'pending' | 'creating' | 'uploading' | 'done' | 'error';

interface BulkRow {
  key:        string;
  file:       File;
  previewUrl: string;
  title:      string;
  notes:      string;
  status:     BulkRowStatus;
  pct:        number;
  errorMsg?:  string;
}

function BulkCreateModal({ onClose, onDone, onExpired }: {
  onClose:   () => void;
  onDone:    (count: number) => void;
  onExpired: () => void;
}) {
  const { taxonomy, loading: taxLoading } = useTaxonomy();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mfrId,    setMfrId]    = useState('');
  const [familyId, setFamilyId] = useState('');
  const [modelId,  setModelId]  = useState('');
  const [revId,    setRevId]    = useState('');
  const [measType, setMeasType] = useState<RefMeasurementType | ''>('');
  const [rows,     setRows]     = useState<BulkRow[]>([]);
  const [saving,   setSaving]   = useState(false);

  // Revoke object URLs on unmount
  useEffect(() => {
    const captured = rows;
    return () => captured.forEach(r => { try { URL.revokeObjectURL(r.previewUrl); } catch {} });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const families  = mfrId    ? familiesForManufacturer(taxonomy, mfrId)  : [];
  const models    = familyId ? modelsForFamily(taxonomy, familyId)        : [];
  const revisions = familyId ? revisionsForFamily(taxonomy, familyId)     : [];

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next: BulkRow[] = Array.from(files)
      .filter(f => f.type.startsWith('image/'))
      .map(f => ({
        key:        `${f.name}-${Date.now()}-${Math.random()}`,
        file:       f,
        previewUrl: URL.createObjectURL(f),
        title:      f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
        notes:      '',
        status:     'pending',
        pct:        0,
      }));
    setRows(prev => [...prev, ...next]);
  };

  const removeRow = (key: string) => {
    setRows(prev => {
      const r = prev.find(x => x.key === key);
      if (r) { try { URL.revokeObjectURL(r.previewUrl); } catch {} }
      return prev.filter(x => x.key !== key);
    });
  };

  const updateRow = (key: string, patch: Partial<BulkRow>) =>
    setRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r));

  const family = taxonomy.device_families.find(f => f.id === familyId);
  const model  = taxonomy.models.find(m => m.id === modelId);
  const board  = taxonomy.board_revisions.find(b => b.id === revId);

  const handleSave = async (publish: boolean) => {
    if (!familyId || !measType) { alert('Select a device family and measurement type first.'); return; }
    if (rows.length === 0)       { alert('No photos added yet.'); return; }
    setSaving(true);
    let doneCount = 0;
    for (const row of rows) {
      if (row.status === 'done') { doneCount++; continue; }
      updateRow(row.key, { status: 'creating', pct: 0 });
      try {
        const payload: RepairReferenceDraft = {
          title:             row.title.trim() || [family?.name, board?.name, measurementTypeLabel(measType)].filter(Boolean).join(' '),
          status:            publish ? 'published' : 'draft',
          seo_title:         '',
          seo_description:   '',
          device_family:     family?.name  ?? '',
          device_model:      model?.name   ?? '',
          board_revision:    board?.name   ?? '',
          device_family_id:  familyId || null,
          model_id:          modelId  || null,
          board_revision_id: revId    || null,
          category:          '',
          measurement_type:  measType,
          testing_conditions: '',
          notes:             row.notes,
          source_type:       'gamefix',
          source_url:        '',
          verification_status:    'observed',
          confirmed_board_count:  1,
          internal_notes:    '',
          tags:              [],
          measurements:      [],
        };
        const created = await createRepairReference(payload);
        updateRow(row.key, { status: 'uploading', pct: 0 });
        await uploadRefMedia(created.id, row.file, pct => updateRow(row.key, { pct }));
        updateRow(row.key, { status: 'done', pct: 100 });
        doneCount++;
      } catch (err) {
        const msg = (err as Error).message;
        if (msg.toLowerCase().includes('unauthorized')) { setSaving(false); onExpired(); return; }
        updateRow(row.key, { status: 'error', errorMsg: msg });
      }
    }
    setSaving(false);
    if (doneCount === rows.length) onDone(doneCount);
  };

  const pendingCount = rows.filter(r => r.status === 'pending' || r.status === 'error').length;
  const doneCount    = rows.filter(r => r.status === 'done').length;
  const canSave      = !saving && rows.length > 0 && !!familyId && !!measType;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-[#0D0D0D] border border-gray-700 rounded-2xl w-full max-w-4xl my-4">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-base font-bold text-[#E0E0E0]">Bulk Create References</h2>
          <button onClick={onClose} disabled={saving} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Shared settings */}
        <div className="px-6 py-4 border-b border-gray-800 bg-gray-900/30">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
            Shared Settings — applies to every photo in this batch
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Manufacturer</label>
              {taxLoading
                ? <div className={sel + ' flex items-center gap-2 text-gray-600'}><Loader className="h-3 w-3 animate-spin" /></div>
                : (
                  <select className={sel} value={mfrId} onChange={e => { setMfrId(e.target.value); setFamilyId(''); setModelId(''); setRevId(''); }}>
                    <option value="">Select…</option>
                    {taxonomy.manufacturers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                )
              }
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Device Family *</label>
              <select className={sel} value={familyId} onChange={e => { setFamilyId(e.target.value); setModelId(''); setRevId(''); }} disabled={!mfrId}>
                <option value="">{mfrId ? 'Select family…' : '—'}</option>
                {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Model</label>
              <select className={sel} value={modelId} onChange={e => setModelId(e.target.value)} disabled={!familyId}>
                <option value="">{familyId ? 'All models' : '—'}</option>
                {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Board Revision</label>
              <select className={sel} value={revId} onChange={e => setRevId(e.target.value)} disabled={!familyId}>
                <option value="">{familyId ? 'All revisions' : '—'}</option>
                {revisions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-2 block">Measurement Type *</label>
            <div className="flex flex-wrap gap-2">
              {REF_MEASUREMENT_TYPES.map(t => (
                <button key={t.value} type="button" onClick={() => setMeasType(t.value)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    measType === t.value
                      ? 'bg-[#FF1744]/15 border-[#FF1744]/60 text-[#FF1744]'
                      : 'bg-black/30 border-gray-700 text-gray-400 hover:border-gray-500'
                  }`}>
                  {t.shortLabel}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Drop zone */}
        <div className="px-6 py-4 border-b border-gray-800">
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) addFiles(e.dataTransfer.files); }}
            className="border-2 border-dashed border-gray-700 hover:border-[#FF1744]/50 rounded-xl p-6 text-center cursor-pointer transition-colors"
          >
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={e => addFiles(e.target.files)} />
            <Upload className="h-6 w-6 text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-400">{rows.length === 0 ? 'Drop photos here or click to browse' : 'Add more photos'}</p>
            <p className="text-xs text-gray-600 mt-1">Each photo becomes one reference — JPG, PNG, WebP</p>
          </div>
        </div>

        {/* Rows */}
        {rows.length > 0 && (
          <div className="px-6 py-4 space-y-2 border-b border-gray-800 max-h-[50vh] overflow-y-auto">
            <p className="text-xs text-gray-500 mb-3">
              {rows.length} photo{rows.length !== 1 ? 's' : ''} — edit titles before saving
            </p>
            {rows.map((row, idx) => (
              <div key={row.key} className={`flex gap-3 bg-gray-900 border rounded-xl p-3 transition-all ${
                row.status === 'done'     ? 'border-green-800/60' :
                row.status === 'error'   ? 'border-red-800/60' :
                row.status === 'creating' || row.status === 'uploading' ? 'border-[#FF1744]/40' :
                'border-gray-800'
              }`}>
                <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800">
                  <img src={row.previewUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-600 flex-shrink-0">{idx + 1}.</span>
                    <input
                      className="flex-1 bg-black/40 border border-gray-700 rounded-lg px-2.5 py-1.5 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
                      placeholder="Reference title…"
                      value={row.title}
                      disabled={row.status !== 'pending' && row.status !== 'error'}
                      onChange={e => updateRow(row.key, { title: e.target.value })}
                    />
                  </div>
                  <input
                    className="w-full bg-black/40 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-400 placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
                    placeholder="Notes (optional)…"
                    value={row.notes}
                    disabled={row.status !== 'pending' && row.status !== 'error'}
                    onChange={e => updateRow(row.key, { notes: e.target.value })}
                  />
                  {row.status === 'creating' && (
                    <div className="flex items-center gap-1.5 text-xs text-[#FF1744]">
                      <Loader className="h-3 w-3 animate-spin" /> Creating record…
                    </div>
                  )}
                  {row.status === 'uploading' && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs text-[#FF1744]">
                        <Loader className="h-3 w-3 animate-spin" /> Uploading {row.pct}%
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-1">
                        <div className="bg-[#FF1744] h-1 rounded-full transition-all" style={{ width: `${row.pct}%` }} />
                      </div>
                    </div>
                  )}
                  {row.status === 'done' && (
                    <div className="flex items-center gap-1 text-xs text-green-400">
                      <Check className="h-3 w-3" /> Saved
                    </div>
                  )}
                  {row.status === 'error' && (
                    <p className="text-xs text-red-400 truncate">{row.errorMsg}</p>
                  )}
                </div>
                {(row.status === 'pending' || row.status === 'error') && (
                  <button onClick={() => removeRow(row.key)}
                    className="text-gray-700 hover:text-red-400 transition-colors flex-shrink-0 self-start mt-0.5">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-gray-600">
            {doneCount > 0
              ? `${doneCount} of ${rows.length} saved`
              : rows.length === 0 ? 'Add photos above' : `${rows.length} photo${rows.length !== 1 ? 's' : ''} ready`}
          </p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onClose} disabled={saving} className={btn('secondary') + ' text-sm'}>
              {doneCount > 0 && pendingCount === 0 ? 'Done' : 'Cancel'}
            </button>
            <button onClick={() => handleSave(false)} disabled={!canSave || pendingCount === 0} className={btn('secondary')}>
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save as Drafts
            </button>
            <button onClick={() => handleSave(true)} disabled={!canSave || pendingCount === 0} className={btn('primary')}>
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Publish All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reference list ────────────────────────────────────────────────────────────

function RefList({ onExpired }: { onExpired: () => void }) {
  const navigate = useNavigate();
  const { references, loading, error, refetch } = useRepairReferences(true);
  const { taxonomy } = useTaxonomy();
  const [search,        setSearch]       = useState('');
  const [statusFilter,  setStatusFilter] = useState('');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [duplicating,   setDuplicating]  = useState<string | null>(null);

  useEffect(() => {
    if (error && error.toLowerCase().includes('unauthorized')) onExpired();
  }, [error, onExpired]);

  const filtered = references.filter(r => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      r.title.toLowerCase().includes(q) ||
      r.board_revision.toLowerCase().includes(q) ||
      r.device_model.toLowerCase().includes(q) ||
      r.device_family.toLowerCase().includes(q);
    const matchS = !statusFilter || r.status === statusFilter;
    return matchQ && matchS;
  });

  const getManufacturer = (r: RepairReference): string => {
    if (!r.device_family_id) return '';
    const family = taxonomy.device_families.find(f => f.id === r.device_family_id);
    if (!family) return '';
    return taxonomy.manufacturers.find(m => m.id === family.manufacturer_id)?.name ?? '';
  };

  const getPrimaryPhoto = (r: RepairReference): string | null => {
    const sorted = (r.repair_reference_media ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
    return sorted[0]?.public_url ?? null;
  };

  const handleDelete = async (r: RepairReference) => {
    if (!confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try {
      await deleteRepairReference(r.id);
      refetch();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert(`Delete failed: ${msg}`);
    }
  };

  const handleDuplicate = async (r: RepairReference) => {
    setDuplicating(r.id);
    try {
      const payload: RepairReferenceDraft = {
        title:             `${r.title} (copy)`,
        status:            'draft',
        seo_title:         '',
        seo_description:   '',
        device_family:     r.device_family,
        device_model:      r.device_model,
        board_revision:    r.board_revision,
        device_family_id:  r.device_family_id  ?? null,
        model_id:          r.model_id          ?? null,
        board_revision_id: r.board_revision_id ?? null,
        category:          r.category          ?? '',
        measurement_type:  r.measurement_type,
        testing_conditions: r.testing_conditions ?? '',
        notes:             r.notes,
        source_type:       r.source_type,
        source_url:        r.source_url         ?? '',
        verification_status:    r.verification_status   ?? 'observed',
        confirmed_board_count:  r.confirmed_board_count ?? 1,
        internal_notes:    r.internal_notes     ?? '',
        tags:              [...(r.tags ?? [])],
        measurements:      [],
      };
      const created = await createRepairReference(payload);
      navigate(`/admin/repair-knowledge-base/ref/${created.id}`);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert(`Duplicate failed: ${msg}`);
    } finally {
      setDuplicating(null);
    }
  };

  const handleQuickPublish = async (r: RepairReference) => {
    try {
      await saveRepairReference(r.id, { status: 'published' } as Partial<RepairReferenceDraft>);
      refetch();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert(`Publish failed: ${msg}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0D0D0D]/95 border-b border-gray-800 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 pt-2.5 flex items-center gap-1.5 text-xs text-gray-600">
          <Link to="/admin/edit-site" className="hover:text-gray-400 transition-colors">Admin</Link>
          <span>›</span>
          <Link to="/admin/repair-knowledge-base" className="hover:text-gray-400 transition-colors">Repair Knowledge Base</Link>
          <span>›</span>
          <span className="text-gray-400">Known-Good References</span>
        </div>
        <div className="max-w-5xl mx-auto px-6 py-2 flex items-center gap-3 flex-wrap">
          <Link to="/admin/repair-knowledge-base"
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors flex-shrink-0">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="text-base font-bold text-[#E0E0E0] flex-1 min-w-0">Known-Good References</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setBulkModalOpen(true)} className={btn('secondary') + ' text-sm'}>
              <Upload className="h-4 w-4" /> Bulk Create
            </button>
            <button onClick={() => navigate('/admin/repair-knowledge-base/ref/new')} className={btn('primary')}>
              <Plus className="h-4 w-4" /> New Reference
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <input
            className="flex-1 min-w-48 bg-black/40 border border-gray-700 rounded-xl px-3 py-2 text-[#E0E0E0] placeholder-gray-600 focus:outline-none text-sm"
            placeholder="Search references…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="internal">Internal</option>
          </select>
          <button onClick={refetch} className="text-xs text-gray-500 hover:text-[#E0E0E0] px-3 py-2 transition-colors">
            Refresh
          </button>
        </div>

        {!loading && references.length > 0 && (
          <p className="text-xs text-gray-600">{filtered.length} of {references.length} reference{references.length !== 1 ? 's' : ''}</p>
        )}

        {loading && (
          <div className="flex justify-center py-12"><Loader className="h-6 w-6 animate-spin text-[#FF1744]" /></div>
        )}
        {error && !error.toLowerCase().includes('unauthorized') && (
          <div className="flex items-center gap-3 bg-red-950/30 border border-red-800 rounded-xl px-4 py-3">
            <p className="text-red-400 text-sm flex-1">{error}</p>
            <button onClick={refetch} className="text-xs px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-[#E0E0E0] transition-colors flex-shrink-0">
              Retry
            </button>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-gray-600">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium text-gray-500">{references.length === 0 ? 'No references yet' : 'No results'}</p>
            {references.length === 0 && (
              <div className="flex gap-3 justify-center mt-4">
                <button onClick={() => setBulkModalOpen(true)} className={btn('secondary')}>
                  <Upload className="h-4 w-4" /> Bulk Create
                </button>
                <button onClick={() => navigate('/admin/repair-knowledge-base/ref/new')} className={btn('primary')}>
                  <Plus className="h-4 w-4" /> New Reference
                </button>
              </div>
            )}
          </div>
        )}

        {/* Reference list */}
        <div className="space-y-2">
          {filtered.map(r => {
            const thumb  = getPrimaryPhoto(r);
            const mfr    = getManufacturer(r);
            const photos = r.repair_reference_media?.length ?? 0;
            const isDupl = duplicating === r.id;
            return (
              <div key={r.id}
                className="bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-2xl flex items-center gap-3 transition-all overflow-hidden">
                {/* Thumbnail */}
                <div className="w-20 h-16 flex-shrink-0 bg-gray-800 overflow-hidden">
                  {thumb
                    ? <img src={thumb} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><FileText className="h-6 w-6 text-gray-700" /></div>
                  }
                </div>

                {/* Info — clickable */}
                <div className="flex-1 min-w-0 py-2.5 cursor-pointer" onClick={() => navigate(`/admin/repair-knowledge-base/ref/${r.id}`)}>
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <p className="font-semibold text-[#E0E0E0] text-sm truncate">{r.title || 'Untitled'}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0 ${
                      r.status === 'published' ? 'text-green-400 bg-green-900/30 border-green-800' :
                      r.status === 'internal'  ? 'text-yellow-400 bg-yellow-900/30 border-yellow-800' :
                      'text-gray-400 bg-gray-800 border-gray-700'
                    }`}>{r.status}</span>
                    {photos > 0 && (
                      <span className="text-[10px] text-gray-600 flex-shrink-0">{photos} photo{photos !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs text-gray-500">
                    {mfr && <span>{mfr}</span>}
                    {r.device_family && <><span>·</span><span>{r.device_family}</span></>}
                    {r.device_model && <><span>·</span><span>{r.device_model}</span></>}
                    {r.board_revision && <><span>·</span><span className="font-mono text-[#00BFFF]">{r.board_revision}</span></>}
                    {r.measurement_type && <><span>·</span><span>{measurementTypeLabel(r.measurement_type)}</span></>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 pr-3 flex-shrink-0">
                  {r.status === 'draft' && (
                    <button onClick={() => handleQuickPublish(r)}
                      className="hidden sm:block text-xs px-2.5 py-1.5 bg-green-900/30 hover:bg-green-900/50 border border-green-800/50 rounded-lg text-green-400 hover:text-green-300 transition-colors">
                      Publish
                    </button>
                  )}
                  <button onClick={() => handleDuplicate(r)} disabled={!!duplicating}
                    className="hidden sm:block text-xs px-2.5 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-[#E0E0E0] transition-colors"
                    title="Duplicate (copy metadata, no photos)">
                    {isDupl ? <Loader className="h-3 w-3 animate-spin" /> : 'Copy'}
                  </button>
                  <button onClick={() => navigate(`/admin/repair-knowledge-base/ref/${r.id}`)}
                    className="text-xs px-2.5 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-[#E0E0E0] transition-colors">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(r)} className="p-1.5 text-gray-600 hover:text-red-400 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {bulkModalOpen && (
        <BulkCreateModal
          onClose={() => setBulkModalOpen(false)}
          onDone={count => { setBulkModalOpen(false); refetch(); alert(`${count} reference${count !== 1 ? 's' : ''} created successfully!`); }}
          onExpired={onExpired}
        />
      )}
    </div>
  );
}

// ── Page wrapper ──────────────────────────────────────────────────────────────

function AdminRefContent({ onExpired }: { onExpired: () => void }) {
  const { refId } = useParams<{ refId?: string }>();
  return refId
    ? <RefEditor refId={refId} onExpired={onExpired} />
    : <RefList   onExpired={onExpired} />;
}

export default function AdminRefEditorPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  const handleExpired = useCallback(() => {
    clearAdminAuth();
    setAuthed(false);
  }, []);
  return authed
    ? <AdminRefContent onExpired={handleExpired} />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
